import { find, has, some } from 'lodash';
import CONSTANTS from '../const/constants';
import { canSeeTemplateAnalysis } from '../views/dashboard/TemplateAnalysisView/access';
import { canSeeInicio, INICIO_ROUTE } from '../views/dashboard/inicio/access';

const protectedRoutes = [
  {
    path: "/chat",
    permissions: ["readChat", "agent"],
  },
  {
    path: "/chat/:chatId",
    permissions: ["readChat", "agent"],
  },
  {
    path: "/boards",
    permissions: ["readChat", "agent"],
  },
  {
    path: "/boards/:id",
    permissions: ["readChat", "agent"],
  },
  {
    path: "/bot",
    permissions: ["writeUsers"],
  },
  {
    path: "/broadcast",
    permissions: ["writeUsers"],
  },
  {
    path: "/broadcast/:currentStep",
    permissions: ["writeUsers"],
  },
  {
    path: "/contacts",
    permissions: ["readChat", "agent"],
  },
  {
    path: "/users",
    permissions: ["readUsers", "writeUsers"],
  },
  {
    path: "/users/new",
    permissions: ["writeUsers"],
  },
  {
    path: "/spaces",
    permissions: ["readSpaces", "writeSpaces"],
  },
  {
    path: "/spaces/new",
    permissions: ["writeSpaces"],
  },
  {
    path: "/spaces/new-whatsapp",
    permissions: ["writeSpaces"],
  },
  {
    path: "/config",
    permissions: [
      "readCompany",
      "writeCompany",
      "readPayments",
      "writePayments",
      "readTags",
      "writeTags",
      "readRols",
      "writeRols",
      "readDataSheetConfiguration",
      "writeDataSheetConfiguration",
    ],
  },
  {
    path: "/config/new-tag",
    permissions: ["writeTags"],
  },
  {
    path: "/config/new-rol",
    permissions: ["writeRols"],
  },
  {
    path: "/config/new-datasheet",
    permissions: ["writeDataSheetConfiguration"],
  },
  {
    path: "/config/new-whatsapp-template",
    permissions: ["whatsappTemplate"],
  },
  {
    path: "/config/edit-plan",
    permissions: ["writePayments"],
  },
  {
    path: "/config/edit-card",
    permissions: ["writePayments"],
  },
  {
    path: "/config/payment",
    permissions: ["writePayments"],
  },
  {
    path: CONSTANTS.MULTIMEDIA_ROUTE,
    permissions: ["agent", "writeMultimedia", "readMultimedia"],
  },
  {
    path: CONSTANTS.ALBUM_ROUTE,
    permissions: ["agent", "writeMultimedia", "readMultimedia"],
  },
  {
    path: CONSTANTS.SUBALBUM_ROUTE,
    permissions: ["agent", "writeMultimedia", "readMultimedia"],
  },
  {
    path: CONSTANTS.QUICK_ANSWERS_ROUTE,
    permissions: ["agent", "readQuickAnswers", "writeQuickAnswers"],
  },
  {
    path: CONSTANTS.QUICK_ANSWERS_ACTION_ROUTE,
    permissions: ["agent", "writeQuickAnswers"],
  },
  {
    path: CONSTANTS.LINK_WHATSAPP_ROUTE,
    permissions: ["writeSpaces"],
  },
  {
    path: CONSTANTS.CONFIG_NEW_WHATSAPPTEMPLATE_ROUTE,
    permissions: ["whatsappTemplate"],
  },
  {
    path: CONSTANTS.UPDATE_SUBSCRIPTION_ROUTE,
    permissions: ["readPayments", "writePayments"],
  },
  {
    path: "/account",
    permissions: ["readCompany", "writeCompany"],
  },
  {
    path: "/panel",
    permissions: ["readCompany"],
  },
  {
    path: "/analytics",
    permissions: ["readCompany", "writeCompany", "readAnalytics"],
  },
  {
    path: "/analytics/agents",
    permissions: [
      "readCompany",
      "writeCompany",
      "readAnalytics",
      "agent",
      "readUsers",
      "writeUsers",
    ],
  },
  {
    path: "/help",
    permissions: ["readCompany", "writeCompany"],
  },
  {
    path: "/broadcast/table",
    permissions: ["campaigns"],
  },
  {
    path: "/broadcast/analytics",
    permissions: ["campaigns"],
  },
  {
    path: "/dashboard",
    permissions: ["readCompany", "writeCompany"],
  },
];

const routeGuard = (path, user) => {
  const { permissions } = user.data;
  const basePath = path.split('/:')[0];

  if (basePath === '/plantillas') {
    return canSeeTemplateAnalysis(user.data);
  }

  // Inicio (tareas para agentes humanos): la misma regla que la entrada del nav.
  if (basePath === INICIO_ROUTE) {
    return canSeeInicio(user.data);
  }

  // Validación especial para /dashboard ("Resumen", AI Summaries)
  if (basePath === '/dashboard') {
    const companyId = user.data?.company?.id;
    const isAiSummariesEnabled = user.data?.company?.isAiSummariesEnabled;
    const roleName = user.data?.rol?.name;
    const isSuperadmin = roleName === CONSTANTS.SUPERADMIN;

    // Para mobieffects iventas: habilitado para todos los usuarios
    if (companyId === CONSTANTS.MOBIEFFECTS_COMPANY_ID) {
      return true;
    }

    // Para otras compañías: solo si isAiSummariesEnabled es true Y es superadmin
    if (isAiSummariesEnabled && isSuperadmin) {
      return true;
    }

    return false;
  }

  const protectedRoute = find(protectedRoutes, ['path', basePath]);
  const credentials = protectedRoute ? protectedRoute.permissions : [];
  const response = some(credentials, (credential) => has(permissions, credential));
  console.log('response toute guard', response);
  return response;
};

// ---------------------------------------------------------------------------
// Partner access: a tab working a sede only supports an allow-list of
// sections. This list decides sede-mode reachability only; the permission
// checks above still apply to every route.
// ---------------------------------------------------------------------------
const CHAT_ROUTE = "/chat";

// The campaign flow steps (`/broadcast/:currentStep`) a sede tab can work:
// only the campaigns table (pause, resume, reschedule). The creation wizard
// sends a channel credential (`pageToken`) the sede projection strips, so
// it stays blocked until the dashboard uses the partner endpoint. The
// analytics page `/broadcast/analytics/:campaignId` is blocked too: it only
// reads from the analytics service, which does not accept the sede
// credential yet.
const SEDE_MODE_BROADCAST_STEPS = ["table"];

// Sections a sede tab can work, with their sub-paths. Each only uses the
// main API / GraphQL with the delegated token, or has its user-identity
// actions hidden in sede mode (contacts: create and import; channels:
// linking, unlinking, FB Leads and space management; settings: the Calendly
// connection; team: removing a member). The statistics (`/analytics` and
// `/analytics/agents`) are open in full: their advanced dashboard reads from
// the analytics service with the delegated token and shows a notice while
// that service refuses it. The team (`/users` and its member form
// `/users/new`) and the help (`/help`, which makes no request) are open too.
// The boards (`/boards` and one board, `/boards/:id`) are read through the
// partner routes, never from the boards service: read-only for now (see
// views/Boards/sedeModeCapabilities.js).
const SEDE_MODE_ROUTES = [
  CHAT_ROUTE,
  "/boards",
  "/contacts",
  CONSTANTS.MULTIMEDIA_ROUTE,
  CONSTANTS.ALBUM_ROUTE,
  CONSTANTS.SUBALBUM_ROUTE,
  CONSTANTS.QUICK_ANSWERS_ROUTE,
  "/users",
  "/spaces",
  "/config",
  "/analytics",
  "/help",
  ...SEDE_MODE_BROADCAST_STEPS.map((step) => `/broadcast/${step}`),
];

// Children of an allowed section that still create or edit identity-bound
// data: the more specific block wins over the section prefix. The role
// editor (`/config/new-rol`) is not one of them: the backend scopes roles to
// the sede's company for a delegated login.
const SEDE_MODE_BLOCKED_ROUTES = [
  "/spaces/new",
  "/spaces/new-whatsapp",
  // Billing screens act on the sede's subscription and card: not for a partner.
  "/config/edit-plan",
  "/config/edit-card",
  "/config/payment",
];

// Routes the router renders without a session. They stay reachable from a
// sede tab; everything else that is not an allowed section is blocked.
const SEDE_MODE_PUBLIC_ROUTES = [
  "/login",
  "/auth",
  "/recoverPassword",
  "/changePassword",
  "/two-factor",
  "/qr-code",
  "/forbidden",
  "/maintenance",
  CONSTANTS.REF_CODE_ROUTE,
];

const isRouteOrChild = (pathname, route) =>
  pathname === route || pathname.startsWith(`${route}/`);

/**
 * Where a navigation has to go instead of `path`, or `null` to let it be.
 * Only a sede tab with a session is ever redirected: the allowed sections
 * (with any sub-path, query string or hash, minus their blocked children)
 * and the public routes are allowed, any other route goes to the chat.
 */
export const getSedeModeRedirect = ({ path, sedeMode, authenticated } = {}) => {
  if (!sedeMode || !authenticated) return null;
  const pathname = String(path || "").split(/[?#]/)[0];
  if (SEDE_MODE_BLOCKED_ROUTES.some((route) => isRouteOrChild(pathname, route))) return CHAT_ROUTE;
  if (SEDE_MODE_ROUTES.some((route) => isRouteOrChild(pathname, route))) return null;
  if (SEDE_MODE_PUBLIC_ROUTES.some((route) => isRouteOrChild(pathname, route))) return null;
  return CHAT_ROUTE;
};

export default routeGuard;
